'use client'

import { useLatest } from '@/core/hooks/use-latest'
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { usePathname } from 'next/navigation'
import {
  LuChevronDown,
  LuImagePlus,
  LuSlidersHorizontal,
  LuUndo2,
  LuWandSparkles,
  LuX,
} from 'react-icons/lu'

import DropdownMenu from '@/core/components/dropdown-menu'
import { Button, buttonStyles } from '@/core/components/form/button/button'
import ErrorToast from '@/core/components/error-toast'
import { Combobox } from '@/core/components/form/combobox'
import { FileInput } from '@/core/components/form/file-input'
import { InputText } from '@/core/components/form/input-text'
import { Switch } from '@/core/components/form/switch'
import { Textarea } from '@/core/components/form/textarea'
import { prefersReducedMotion } from '@/core/lib/motion'
import { toast } from '@/core/lib/toast'
import { cn } from '@/core/utils/common'

import { expandField } from '@/features/veluvit/services/creator-ai.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { isUploadedVideo } from '@/features/studio-renew/media'
import { shrinkStudioImage } from '@/features/studio-renew/image-resize'
import { MAX_TAG_LENGTH, MAX_TAGS } from '@/features/studio-renew/tags'
import { StepTitleHidden } from '@/features/studio-renew/components/step-head-context'
import { useStudioValidation } from '@/features/studio-renew/components/validation-session'

import type {
  ExpandContext,
  ExpandField,
} from '@/features/veluvit/services/creator-ai.service'
import type { ComponentProps, ReactNode } from 'react'

/** 입력이 멈추면 저장 — 한 글자마다 저장소(이미지 포함 JSON)를 다시 쓰지 않게 */
const SHINE =
  'bg-[linear-gradient(105deg,transparent_40%,rgb(255_255_255/0.7)_50%,transparent_60%)] bg-[length:250%_100%] bg-no-repeat animate-[shine_2.4s_ease-in-out_infinite] motion-reduce:animate-none'

const COMMIT_MS = 500
const DraftInputContext = createContext(false)

/** 새 폼은 로컬 원본에 즉시 반영하고, 서버 요청 간격은 sync의 debounce가 관리한다. */
export function DraftInputBoundary({
  immediate,
  children,
}: {
  immediate: boolean
  children: ReactNode
}) {
  return (
    <DraftInputContext.Provider value={immediate}>
      {children}
    </DraftInputContext.Provider>
  )
}

function useDraft(value: string, onCommit: (v: string) => void) {
  // 새 작품은 값이 비워지는 즉시 로컬 원본에도 반영해야 빈 내용으로 생성하지 않는다.
  const fromDraftForm = useContext(DraftInputContext)
  const newWork = usePathname()?.startsWith('/studio-renew/work/new/') ?? false
  const immediate = fromDraftForm || newWork
  const [draft, setDraft] = useState(value)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useLatest(onCommit)
  // 밖에서 값이 바뀌면(다른 단계에서 수정·복제) 따라간다 — 입력 중이 아닐 때만
  useEffect(() => {
    if (!timer.current) setDraft(value)
  }, [value])
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )
  const change = (v: string) => {
    setDraft(v)
    if (timer.current) clearTimeout(timer.current)
    if (immediate) {
      timer.current = null
      latest.current(v)
      return
    }
    timer.current = setTimeout(() => {
      timer.current = null
      latest.current(v)
    }, COMMIT_MS)
  }
  const flush = () => {
    if (!timer.current) return
    clearTimeout(timer.current)
    timer.current = null
    latest.current(draft)
  }
  // 바로 저장 — AI 결과 · 되돌리기처럼 입력 멈춤을 기다리지 않는 교체
  const set = (v: string) => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    setDraft(v)
    latest.current(v)
  }
  return { draft, change, flush, set }
}

/** 스튜디오 버튼 톤 → core Button variant. reward 는 금색(accent), soft 는 보조와 같은 회색(secondary), danger 는 글자만 빨강 */
type BtnTone = 'primary' | 'soft' | 'secondary' | 'ghost' | 'danger' | 'reward'
const TONE: Record<
  BtnTone,
  NonNullable<ComponentProps<typeof Button>['variant']>
> = {
  primary: 'primary',
  reward: 'accent',
  secondary: 'secondary',
  soft: 'secondary',
  ghost: 'ghost',
  danger: 'ghost',
}

export function Btn({
  tone = 'secondary',
  size = 'md',
  className,
  ...rest
}: Omit<ComponentProps<typeof Button>, 'variant' | 'size'> & {
  tone?: BtnTone
  size?: 'sm' | 'md' | 'icon' | 'icon-sm'
}) {
  return (
    <Button
      variant={TONE[tone]}
      size={size}
      bold={tone === 'primary' || tone === 'reward'}
      className={cn(tone === 'danger' && 'text-error!', className)}
      {...rest}
    />
  )
}

/** 단계 머리 — 기본 글꼴의 제목(모바일 20 / PC 24) · 오른쪽 설정 + 한 줄 안내 */
export function StepHead({
  title,
  desc,
  optional = false,
  right,
}: {
  title: string
  desc?: ReactNode
  optional?: boolean
  right?: ReactNode
}) {
  const titleHidden = useContext(StepTitleHidden)
  if (titleHidden)
    return desc ? (
      <p className="mb-5 max-w-[60ch] text-c14 leading-[1.6] text-description over10:text-c15">
        {desc}
      </p>
    ) : null
  return (
    <div data-studio-tour="editor-body" className="mb-8">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <h2 className="text-c20 leading-7 font-bold text-foreground over10:text-c24 over10:leading-8">
          {title}
          {optional && (
            <span className="ml-2.5 text-c14 font-normal text-description over10:text-c15">
              선택사항
            </span>
          )}
        </h2>
        {right}
      </div>
      {desc && (
        <p className="mt-1.5 max-w-[60ch] text-c14 leading-[1.6] text-description over10:text-c15">
          {desc}
        </p>
      )}
    </div>
  )
}

/**
 * 선택 항목 묶음 — 스튜디오의 접는 영역은 전부 이 모양 하나(스플릿 ‘고급 설정’ 결): 테두리 상자 안에 아이콘 · 제목 · ‘선택’ ·
 * 안에 든 항목 한 줄 + 화살표. 상자 전체가 눌리는 칸으로 보이고, 펼치면 같은 상자 안에 칸이 열린다.
 * 채운 내용이 있으면 처음부터 펼친다(채운 값이 접혀 안 보이는 일이 없게).
 */
export function Fold({
  title,
  summary,
  open,
  className,
  children,
}: {
  title: string
  summary?: string
  open?: boolean
  className?: string
  children: ReactNode
}) {
  // 처음 열림만 정한다 — 안의 글을 지우는 순간 접히지 않게, 그 뒤로는 작가가 연 상태를 따른다
  const [initialOpen] = useState(open)
  return (
    <details
      open={initialOpen}
      className={cn(
        'group rounded-panel border border-border',
        className ?? 'mb-12'
      )}
    >
      <summary className="flex min-h-16 cursor-pointer list-none items-center gap-3 rounded-panel px-4 py-3 outline-none transition-colors duration-150 hover:bg-surface-raised/50 focus-visible:bg-surface-raised/60 group-open:rounded-b-none [&::-webkit-details-marker]:hidden">
        <LuSlidersHorizontal
          aria-hidden="true"
          className="size-5 flex-none text-point"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-c15 font-semibold text-foreground over10:text-c16">
            {title}
            <span className="ml-2 text-c13 font-normal text-description over10:text-c14">
              선택사항
            </span>
          </span>
          {summary && (
            <span className="mt-0.5 block truncate text-c13 text-description over10:text-c14">
              {summary}
            </span>
          )}
        </span>
        <LuChevronDown
          aria-hidden="true"
          className="size-5 flex-none text-description transition-[rotate] duration-200 group-open:rotate-180"
        />
      </summary>
      <div className="grid grid-cols-1 gap-8 border-t border-border px-4 pt-5 pb-6">
        {children}
      </div>
    </details>
  )
}

/** 켜고 끄는 선택 묶음 — Fold 와 같은 상자. 화살표 자리에 스위치, 켜면 같은 상자 안에 칸이 열린다 */
export function SwitchFold({
  title,
  summary,
  on,
  onChange,
  label,
  busy = false,
  children,
}: {
  title: string
  summary?: string
  on: boolean
  onChange: (on: boolean) => void
  label: string
  busy?: boolean
  children: ReactNode
}) {
  return (
    <section
      aria-label={title}
      className="mb-12 rounded-panel border border-border"
    >
      <div className="flex min-h-16 items-center gap-3 px-4 py-3">
        <LuSlidersHorizontal
          aria-hidden="true"
          className="size-5 flex-none text-point"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-c15 font-semibold text-foreground over10:text-c16">
            {title}
            <span className="ml-2 text-c13 font-normal text-description over10:text-c14">
              선택사항
            </span>
          </span>
          {summary && (
            <span className="mt-0.5 block truncate text-c13 text-description over10:text-c14">
              {summary}
            </span>
          )}
        </span>
        <span
          aria-busy={busy}
          className={
            busy ? 'pointer-events-none flex-none opacity-50' : 'flex-none'
          }
        >
          <Toggle on={on} onChange={onChange} label={label} />
        </span>
      </div>
      {on && (
        <div className="grid grid-cols-1 gap-8 border-t border-border px-4 pt-5 pb-6">
          {children}
        </div>
      )}
    </section>
  )
}

/**
 * 칸 묶음 — 면 없이 간격으로만 나눈다(2026-10-01 사용자: “이상한 배경의 카드 · 보더로 찍찍” 거절).
 * 편집 화면의 면은 한 겹뿐 — 입력칸(#2c2c2e)과 대화방 미리보기. 묶음 사이 48, 칸 사이 32.
 */
export function Card({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  // grid-cols-1(minmax(0,1fr)) — 말줄임(truncate) 글이 긴 줄이 들어와도 칸이 글 길이만큼 늘어나 가로 스크롤을 만들지 않게
  return (
    <section className={cn('mb-12 grid grid-cols-1 gap-8', className)}>
      {children}
    </section>
  )
}

/** 묶음 안 한 칸 — 라벨(필수 * · 선택 표시 · 오른쪽 도구) → 한 줄 안내 → 입력. 안내는 입력만으로 알 수 없는 것만 적는다 */
const FieldContext = createContext<{
  invalid: boolean
  required: boolean
  labelId?: string
}>({ invalid: false, required: false })

export function Field({
  label,
  required = false,
  optional = false,
  invalid: missing = false,
  hint,
  right,
  children,
  htmlFor,
}: {
  label: ReactNode
  required?: boolean
  optional?: boolean
  invalid?: boolean
  hint?: ReactNode
  right?: ReactNode
  children: ReactNode
  htmlFor?: string
}) {
  const labelId = useId()
  const { attempted } = useStudioValidation()
  const invalid = attempted && missing
  return (
    <FieldContext.Provider value={{ invalid, required, labelId }}>
      <div
        data-studio-tour-label={typeof label === 'string' ? label : undefined}
        role="group"
        aria-labelledby={labelId}
        data-studio-invalid={invalid || undefined}
        tabIndex={invalid ? -1 : undefined}
        className="scroll-mt-6"
      >
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <label
            id={labelId}
            htmlFor={htmlFor}
            className={cn(
              'text-c15 font-semibold transition-colors over10:text-c16',
              invalid ? 'text-error' : 'text-foreground'
            )}
          >
            {label}
            {required && (
              <span className="ml-0.5 text-error" aria-hidden="true">
                *
              </span>
            )}
            {optional && (
              <span className="ml-2 text-c13 font-normal text-description over10:text-c14">
                선택사항
              </span>
            )}
            {invalid && (
              <span className="sr-only"> — 필수 항목을 확인해 주세요</span>
            )}
          </label>
          {right}
        </div>
        {hint && (
          <p className="mt-0.5 text-c13 leading-[1.6] text-description over10:text-c14">
            {hint}
          </p>
        )}
        <div className="mt-2.5">{children}</div>
      </div>
    </FieldContext.Provider>
  )
}

/** 글자 수 — max 가 있으면 n/max, 없으면 n자(긴 글은 막지 않는다, §14.1) */
function Counter({ n, max }: { n: number; max?: number }) {
  if (max == null)
    return (
      <span className="text-c12 text-description tabular-nums over10:text-c14">
        {n.toLocaleString()}자
      </span>
    )
  return (
    <span
      className={cn(
        'text-c12 tabular-nums over10:text-c14',
        n > max ? 'text-error' : 'text-description'
      )}
    >
      {n.toLocaleString()}/{max.toLocaleString()}
    </span>
  )
}

/** 한 줄 입력 — core InputText + 입력 멈춤 저장 + 오른쪽 글자 수 */
export function TextField({
  id,
  value,
  onChange,
  max,
  placeholder,
  disabled,
  className,
  list,
}: {
  id?: string
  value: string
  onChange: (v: string) => void
  max: number
  placeholder?: string
  disabled?: boolean
  className?: string
  list?: string
}) {
  const { draft, change, flush } = useDraft(value, onChange)
  const field = useContext(FieldContext)
  return (
    <div className={cn('relative', className)}>
      <InputText
        id={id}
        list={list}
        value={draft}
        maxLength={max}
        placeholder={placeholder}
        disabled={disabled}
        aria-labelledby={field.labelId}
        aria-required={field.required || undefined}
        aria-invalid={field.invalid || undefined}
        onChange={(e) => change(e.target.value)}
        onBlur={flush}
        className={cn(max >= 1000 ? 'pr-28' : 'pr-20')}
      />
      <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2">
        <Counter n={draft.length} max={max} />
      </span>
    </div>
  )
}

/**
 * 긴 글 — core Textarea. 글자 수 제한 없음(협업 1차 피드백), 카운터는 몇 자인지만. 짧은 문장 칸만 max 로 제한한다.
 * kind 를 주면 칸 아래 줄에 `{user} 넣기` · `AI로 늘리기` · 되돌리기가 붙는다(모든 긴 글 칸이 같이 쓰는 공용 도구줄). places 를 주면 `장소 넣기` 도 붙는다.
 * AI 로 늘리기: 칸의 짧은 메모 + context(이름 등)를 보내 확장 글로 교체 → 덮개 + 흐르는 빛 → 글자가 차례로 써진다. 되돌리기는 한 번.
 */
export function TextArea({
  id,
  value,
  onChange,
  rows = 5,
  max,
  placeholder,
  mono,
  kind,
  context,
  places,
}: {
  id?: string
  value: string
  onChange: (v: string) => void
  rows?: number
  max?: number
  placeholder?: string
  mono?: boolean
  kind?: ExpandField
  context?: ExpandContext
  places?: string[]
}) {
  const { draft, change, flush, set } = useDraft(value, onChange)
  const field = useContext(FieldContext)
  const pathname = usePathname()
  const identity = useSessionStore((state) => state.identity)
  const box = useRef<HTMLTextAreaElement>(null)
  const typer = useRef<ReturnType<typeof setInterval> | null>(null)
  const active = useRef<AbortController | null>(null)
  const latestDraft = useLatest(draft)
  // thinking = 생성 중(덮개) · typing = 결과를 쓰는 중. shown 은 쓰는 중 보이는 글
  const [phase, setPhase] = useState<'thinking' | 'typing' | null>(null)
  const [shown, setShown] = useState('')
  // 늘리기 직전 글 — 한 번 되돌리기
  const [prev, setPrev] = useState<string | null>(null)
  const [expandError, setExpandError] = useState('')
  const expandScope = `${identity}:${pathname}`
  const [seenExpandScope, setSeenExpandScope] = useState(expandScope)
  if (seenExpandScope !== expandScope) {
    setSeenExpandScope(expandScope)
    setPhase(null)
    setShown('')
    setPrev(null)
    setExpandError('')
  }
  useEffect(() => {
    return () => {
      active.current?.abort()
      active.current = null
      if (typer.current) clearInterval(typer.current)
      typer.current = null
    }
  }, [identity, pathname])

  const insertText = (text: string) => {
    const el = box.current
    const at = el?.selectionStart ?? draft.length
    const end = el?.selectionEnd ?? at
    change(`${draft.slice(0, at)}${text}${draft.slice(end)}`)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(at + text.length, at + text.length)
    })
  }

  const expand = async () => {
    if (!kind || phase || active.current) return
    if (!draft.trim()) {
      setExpandError('한 줄이라도 아이디어를 적어 주세요.')
      return
    }
    flush()
    const before = draft
    const controller = new AbortController()
    active.current = controller
    const current = () =>
      active.current === controller && !controller.signal.aborted
    const changed = () => {
      if (latestDraft.current === before) return false
      setPhase(null)
      setExpandError(
        '생성 중 입력 내용이 바뀌어 확장 결과를 덮어쓰지 않았어요.'
      )
      active.current = null
      return true
    }
    setExpandError('')
    setShown('')
    setPhase('thinking')
    try {
      const workId = /^\/studio-renew\/work\/([^/]+)\//.exec(
        pathname || ''
      )?.[1]
      const { text } = await expandField(
        {
          field: kind,
          draft: before,
          context,
          workId,
          target: `${pathname}:${id || field.labelId || kind}`,
        },
        { signal: controller.signal }
      )
      if (!current() || changed()) return
      setPrev(before)
      const chars = Array.from(text)
      const step = Math.max(2, Math.ceil(chars.length / 90))
      if (prefersReducedMotion()) {
        set(text)
        setPhase(null)
        active.current = null
        return
      }
      setPhase('typing')
      let n = 0
      typer.current = setInterval(() => {
        if (!current() || changed()) {
          if (typer.current) clearInterval(typer.current)
          typer.current = null
          return
        }
        n = Math.min(chars.length, n + step)
        setShown(chars.slice(0, n).join(''))
        if (n < chars.length) return
        if (typer.current) clearInterval(typer.current)
        typer.current = null
        set(text)
        setPhase(null)
        active.current = null
      }, 24)
    } catch (cause) {
      if (!current()) return
      active.current = null
      setPhase(null)
      setExpandError(
        cause instanceof Error
          ? cause.message
          : 'AI로 늘리지 못했어요. 원문은 그대로 유지했어요.'
      )
    }
  }

  const undo = () => {
    if (prev == null) return
    set(prev)
    setPrev(null)
  }

  return (
    <div>
      <div className="relative">
        <Textarea
          ref={box}
          id={id}
          aria-labelledby={field.labelId}
          aria-required={field.required || undefined}
          aria-invalid={field.invalid || undefined}
          rows={rows}
          maxLength={max}
          value={phase === 'typing' ? shown : draft}
          readOnly={!!phase}
          placeholder={placeholder}
          spellCheck={!mono}
          onChange={(e) => change(e.target.value)}
          onBlur={flush}
          classNames={{
            textarea: cn(
              'min-h-0! leading-[1.65]',
              mono && 'font-mono text-c13 over10:text-c14!'
            ),
          }}
        />
        {phase === 'thinking' && (
          <div
            role="status"
            className="pointer-events-none absolute inset-0 grid place-items-center overflow-hidden rounded-field bg-black/55 backdrop-blur-[2px]"
          >
            <span
              aria-hidden="true"
              className={cn('absolute inset-0 opacity-50', SHINE)}
            />
            <span className="relative inline-flex items-center gap-1.5 text-c14 font-semibold text-sub">
              <LuWandSparkles aria-hidden="true" className="size-4" />
              AI가 늘리는 중…
            </span>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {kind && (
          <>
            <Btn
              tone="soft"
              size="sm"
              disabled={!!phase}
              onClick={() => insertText('{user}')}
              className="rounded-full!"
            >
              {'{user}'} 넣기
            </Btn>
            {places &&
              (phase || !places.length ? (
                <Btn tone="soft" size="sm" disabled className="rounded-full!">
                  장소 넣기
                </Btn>
              ) : (
                <DropdownMenu
                  className="max-h-72 overflow-y-auto"
                  items={places.map((name) => ({
                    id: name,
                    label: name,
                    onClick: () => insertText(name),
                  }))}
                  trigger={
                    <span
                      className={buttonStyles({
                        variant: TONE.soft,
                        size: 'sm',
                        className: 'rounded-full!',
                      })}
                    >
                      장소 넣기
                    </span>
                  }
                />
              ))}
            <Btn
              tone="soft"
              size="sm"
              disabled={!!phase}
              onClick={() => void expand()}
              className="rounded-full!"
            >
              <LuWandSparkles aria-hidden="true" className="size-4" />
              AI로 늘리기
            </Btn>
            {prev != null && prev !== draft && (
              <Btn
                tone="ghost"
                size="sm"
                disabled={!!phase}
                onClick={undo}
                className="rounded-full!"
              >
                <LuUndo2 aria-hidden="true" className="size-4" />
                되돌리기
              </Btn>
            )}
          </>
        )}
        <span className="ml-auto">
          <Counter n={(phase === 'typing' ? shown : draft).length} max={max} />
        </span>
      </div>
      <ErrorToast message={expandError} />
    </div>
  )
}

/**
 * 설명이 붙는 큰 선택지 — core Radio 는 한 줄 라벨만 받아 설명 줄을 담지 못해 여기서 조합한다.
 * 칸 테두리는 입력 컨트롤의 경계(카드 아님). 고르면 장미 테두리 + 채운 구슬.
 */
export function Choice<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled,
  wide,
}: {
  value: T | null
  options: { v: T; title: string; desc?: string }[]
  onChange: (v: T) => void
  label: string
  disabled?: boolean
  wide?: boolean
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('grid gap-2', wide && 'over10:grid-cols-2')}
    >
      {options.map((o) => (
        <Button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          disabled={disabled}
          onClick={() => onChange(o.v)}
          className={cn(
            'flex cursor-pointer items-start gap-3 rounded-field border px-4 py-3.5 text-left outline-none transition-[background-color,border-color] duration-150 disabled:cursor-not-allowed disabled:opacity-50',
            value === o.v
              ? 'border-point bg-brand-soft'
              : 'border-transparent bg-surface-raised hover:bg-surface-strong focus-visible:bg-surface-strong'
          )}
          variant="bare"
        >
          <span
            aria-hidden="true"
            className={cn(
              'mt-0.5 grid size-5 flex-none place-items-center rounded-full border-2',
              value === o.v ? 'border-point' : 'border-line-strong'
            )}
          >
            {value === o.v && (
              <span className="size-2.5 rounded-full bg-point" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-c15 font-semibold text-foreground over10:text-c16">
              {o.title}
            </span>
            {o.desc && (
              <span className="mt-0.5 block text-c13 leading-[1.55] text-description over10:text-c14">
                {o.desc}
              </span>
            )}
          </span>
        </Button>
      ))}
    </div>
  )
}

/**
 * 몇 개 중 하나(난이도 · 보기 방식) — core 에 세그먼트 컨트롤이 없어(Tabs 는 패널 전환용) 여기서 조합한다.
 * 홈 안에서 고른 칸만 채워진다. 높이는 폼 컨트롤 규격(h-12).
 */
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: { v: T; label: string }[]
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex h-10 max-w-full gap-0.5 overflow-x-auto rounded-field bg-surface-raised p-0.5 [scrollbar-width:none]"
    >
      {options.map((o) => (
        <Button
          key={String(o.v)}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          onClick={() => onChange(o.v)}
          className={cn(
            'min-w-16 flex-none cursor-pointer rounded-control px-4 text-c14 whitespace-nowrap outline-none transition-[background-color,color] duration-200 over10:text-c15',
            value === o.v
              ? 'bg-t4 font-semibold text-foreground shadow-[0_1px_4px_rgb(0_0_0/0.35)]'
              : 'text-gray hover:text-foreground focus-visible:text-foreground focus-visible:underline'
          )}
          variant="bare"
        >
          {o.label}
        </Button>
      ))}
    </div>
  )
}

/** 켜기/끄기 — core Switch */
export function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <Switch
      checked={on}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
    />
  )
}

/** 고르기 — core Combobox(Base UI Select). ghost 는 정렬처럼 줄 끝에 붙는 글자형 선택 */
export function Select<T extends string>({
  id,
  value,
  options,
  onChange,
  label,
  disabled,
  variant,
  className,
}: {
  id?: string
  value: T
  options: { v: T; label: string }[]
  onChange: (v: T) => void
  label: string
  disabled?: boolean
  variant?: 'field' | 'ghost'
  className?: string
}) {
  return (
    <Combobox
      id={id}
      ariaLabel={label}
      value={value}
      options={options.map((o) => ({ value: o.v, label: o.label }))}
      onChange={(v) => onChange(v as T)}
      disabled={disabled}
      variant={variant}
      className={className}
    />
  )
}

/** 이미지 올리기 — 긴 변 720px 로 줄여 저장해 저장소·미리보기를 가볍게 */
const MAX_BYTES = 5 * 1024 * 1024
/**
 * 움직이는 표지(2차 결정: gif · 영상은 작품 표지만) — GIF · MP4, 5초 이하.
 * 고른 파일은 data URL 로 들고 있다가 저장할 때 서버에 올린다(media.ts · sync.ts). 상한(3MB)은 기기 저장 때 값 그대로다 — 서버 상한은 backend.md 5-5.
 */
const MOTION_MAX_BYTES = 3 * 1024 * 1024
const MOTION_MAX_SEC = 5

const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

/** 영상 길이(초) — 메타데이터만 읽는다 */
const videoSeconds = (src: string) =>
  new Promise<number>((resolve, reject) => {
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.onloadedmetadata = () => resolve(v.duration)
    v.onerror = () => reject(new Error('video'))
    v.src = src
  })

/** 영상인가 — 아직 안 올린 data URL, 또는 서버에 올린 MP4(주소에 확장자가 없어 media.ts 가 기억한다) */
export const isVideoSrc = (src?: string) =>
  !!src && (src.startsWith('data:video/') || isUploadedVideo(src))

/** 파일 고르기 — 숨은 input 과 여는 함수. 형식·용량 검사 후 줄인 data URL 을 넘긴다. motion 이면 GIF · 영상도 받는다(줄이지 않음 — 캔버스로 줄이면 멈춘 그림이 된다) */
/** maxEdge — 자르기 원본처럼 나중에 다시 잘라 쓸 그림은 더 크게(기본 720) */
export function useImagePicker(
  onChange: (v: string) => void,
  label: string,
  {
    motion = false,
    gifOnly = false,
    maxEdge,
  }: { motion?: boolean; gifOnly?: boolean; maxEdge?: number } = {}
) {
  const ref = useRef<HTMLInputElement>(null)
  let accept = 'image/png,image/jpeg,image/webp'
  if (motion) accept += ',image/gif,video/mp4'
  if (gifOnly) accept = 'image/gif'
  const pick = async (file?: File) => {
    if (!file) return
    if (gifOnly && file.type !== 'image/gif')
      return toast.show('탐색 표지는 GIF 파일만 올릴 수 있어요')
    const moving =
      (motion || gifOnly) && /^(image\/gif|video\/mp4)$/.test(file.type)
    if (!moving && !/^image\/(png|jpe?g|webp)$/.test(file.type))
      return toast.show(
        motion
          ? 'PNG · JPG · WEBP · GIF 이미지나 MP4 영상만 올릴 수 있어요'
          : 'PNG · JPG · WEBP 이미지만 올릴 수 있어요'
      )
    if (moving) {
      if (file.size > MOTION_MAX_BYTES)
        return toast.show('움직이는 표지는 3MB 이하만 올릴 수 있어요')
      try {
        const src = await readDataUrl(file)
        if (
          file.type.startsWith('video/') &&
          (await videoSeconds(src)) > MOTION_MAX_SEC + 0.5
        )
          return toast.show(`영상은 ${MOTION_MAX_SEC}초 이하만 올릴 수 있어요`)
        onChange(src)
      } catch {
        toast.show('파일을 읽지 못했어요. 다른 파일로 시도해 주세요.')
      }
      return
    }
    if (file.size > MAX_BYTES)
      return toast.show('5MB 이하 이미지만 올릴 수 있어요')
    // 움직이는 WebP 도 여기로 온다 — 정지 이미지로 줄여 저장한다(움직이는 표지는 GIF · MP4 로)
    try {
      onChange(await shrinkStudioImage(file, maxEdge))
    } catch {
      toast.show('이미지를 읽지 못했어요. 다른 파일로 시도해 주세요.')
    }
  }
  const input = (
    // 파일 고르기는 숨은 input 이 브라우저 대화상자를 여는 유일한 방법이라 core 입력으로 대체하지 않는다
    <FileInput
      ref={ref}
      accept={accept}
      className="hidden"
      aria-label={label}
      onChange={(e) => {
        void pick(e.target.files?.[0])
        // 같은 파일을 다시 골라도 onChange 가 오도록 비운다
        e.target.value = ''
      }}
    />
  )
  return { open: () => ref.current?.click(), input }
}

/**
 * 표지 그리기 — 영상(data:video)이면 소리 없는 반복 영상, 아니면 이미지. GIF 는 이미지로 그대로 움직인다.
 * play: auto = 바로 재생(편집 · 미리보기), hover = 올렸을 때만(목록 카드 — 여러 개가 한꺼번에 돌지 않게), still = 첫 장면만(작은 썸네일)
 */
export function CoverMedia({
  src,
  className,
  play = 'auto',
}: {
  src?: string
  className?: string
  play?: 'auto' | 'hover' | 'still'
}) {
  if (!src) return null
  if (!isVideoSrc(src)) {
    // data URL 미리보기라 최적화 대상이 아니다
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={src} alt="" className={className} />
  }
  return (
    <video
      src={src}
      muted
      loop
      playsInline
      autoPlay={play === 'auto'}
      preload={play === 'auto' ? 'auto' : 'metadata'}
      aria-hidden="true"
      className={className}
      onMouseEnter={
        play === 'hover'
          ? (e) => void e.currentTarget.play().catch(() => {})
          : undefined
      }
      onMouseLeave={
        play === 'hover'
          ? (e) => {
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0
            }
          : undefined
      }
    />
  )
}

export function ImageInput({
  value,
  onChange,
  label,
  emptyLabel = label,
  ratio = 'aspect-[2/3]',
  fit = 'cover',
  gifOnly = false,
  className,
}: {
  value: string
  onChange: (v: string) => void
  label: string
  /** 작은 업로드 칸에 표시할 짧은 문구. 접근 가능한 이름은 label을 유지한다. */
  emptyLabel?: string
  ratio?: string
  /** contain — 로고처럼 잘리면 안 되는 그림(칸 안에 통째로 보인다) */
  fit?: 'cover' | 'contain'
  gifOnly?: boolean
  className?: string
}) {
  const picker = useImagePicker(onChange, label, { gifOnly })
  return (
    <div
      className={cn(
        'relative w-28 flex-none self-start overflow-hidden rounded-field bg-surface-raised',
        ratio,
        className
      )}
    >
      {value ? (
        <>
          {/* data URL 미리보기라 최적화 대상이 아니다 */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={value}
            alt=""
            className={cn(
              'size-full',
              fit === 'contain' ? 'object-contain p-2' : 'object-cover'
            )}
          />
          <Btn
            tone="ghost"
            size="icon-sm"
            shape="pill"
            aria-label={`${label} 지우기`}
            onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-black/70 text-white! hover:bg-black/80!"
          >
            <LuX aria-hidden="true" className="size-4" />
          </Btn>
        </>
      ) : (
        <Btn
          tone="ghost"
          onClick={picker.open}
          aria-label={label}
          className="size-full min-h-0! flex-col gap-1! rounded-none! px-2! text-c12! over10:text-c14! text-description!"
        >
          <LuImagePlus aria-hidden="true" className="size-5 flex-none" />
          <span className="max-w-full text-center leading-snug break-keep">
            {emptyLabel}
          </span>
        </Btn>
      )}
      {picker.input}
    </div>
  )
}

/** 태그 — 작가가 직접 적는다. 칩은 입력칸 아래에 최근 추가한 것부터 쌓인다 */
export function TagPicker({
  value,
  onChange,
}: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [text, setText] = useState('')
  const full = value.length >= MAX_TAGS
  const add = () => {
    const tag = text.replace(/^#+/, '').trim()
    if (tag && !value.includes(tag) && !full) onChange([tag, ...value])
    setText('')
  }
  return (
    <div>
      <InputText
        value={text}
        maxLength={MAX_TAG_LENGTH}
        disabled={full}
        aria-label="태그"
        placeholder={
          full ? `최대 ${MAX_TAGS}개` : '태그 입력 후 Enter · 예) 소꿉친구'
        }
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
          event.preventDefault()
          add()
        }}
      />
      {value.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {value.map((tag) => (
            <li
              key={tag}
              className="inline-flex h-8 max-w-full animate-[chip-in_150ms_ease-out] items-center gap-1 rounded-full bg-surface-raised pr-1 pl-3 text-c13 text-foreground over10:h-9 over10:text-c14"
            >
              <span className="min-w-0 truncate">#{tag}</span>
              <Btn
                tone="ghost"
                size="icon-sm"
                shape="pill"
                aria-label={`${tag} 삭제`}
                onClick={() => onChange(value.filter((item) => item !== tag))}
                className="flex-none text-description!"
              >
                <LuX aria-hidden="true" className="size-3.5" />
              </Btn>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
